<template>
  <div class="container">
    <Title>User Logins</Title>

    <div v-if="loading" class="loading">
      Loading...
    </div>

    <div v-else-if="error" class="error">
      {{ error }}
    </div>

    <!-- Table section -->
    <div v-if="sortedUsers.length > 0" class="card-section">
      <h3>User Login History</h3>
      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Login History</th>
              <th>Last Login</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.id">
              <td>{{ user.username }}</td>
              <td>{{ user.role }}</td>
              <td>{{ user.organization_name || 'N/A' }}</td>
              <td>
                <div class="login-history">
                  <span v-for="(timestamp, index) in user.login_timestamps" :key="index" class="login-timestamp">
                    {{ formatDate(timestamp) }}
                  </span>
                </div>
              </td>
              <td>{{ user.last_login ? formatDate(user.last_login) : 'Never' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else-if="!loading" class="no-data card-section">
      No user login data available.
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import { ref, computed } from 'vue';
import Title from './components/Title.vue';

export default {
  name: 'ShowLoginsPage',
  components: {
    Title
  },
  setup() {
    const users = ref([]);
    const loading = ref(true);
    const error = ref(null);

    const fetchUserLogins = async () => {
      try {
        loading.value = true;
        error.value = null;

        console.log('Fetching user logins...');
        const response = await apiService.fetchUserLogins();

        console.log('Raw response:', response);

        if (!response || !response.users) {
          throw new Error(response?.error || 'Invalid server response');
        }

        users.value = response.users;

      } catch (err) {
        console.error('Error fetching logins:', err);
        error.value = err.message || 'Failed to load user logins';
        users.value = [];
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      try {
        // Swiss date and time format: DD.MM.YYYY HH:mm
        return new Date(timestamp).toLocaleString('de-CH', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false // 24-hour format
        });
      } catch (e) {
        console.error('Error formatting timestamp:', e);
        return timestamp;
      }
    };

    const sortedUsers = computed(() => {
      return Array.isArray(users.value) ?
        [...users.value].sort((a, b) => {
          // Сортировка по времени последнего входа (сначала недавние)
          if (!a.last_login && b.last_login) return 1;
          if (a.last_login && !b.last_login) return -1;
          if (!a.last_login && !b.last_login) return 0;
          return new Date(b.last_login) - new Date(a.last_login);
        }) : [];
    });

    // Fetch data when component is mounted
    fetchUserLogins();

    return {
      loading,
      error,
      sortedUsers,
      formatDate
    };
  }
};
</script>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
  color: #323232;
}

.title {
  color: #323232;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #42b983;
  padding-bottom: 0.5rem;
  display: inline-block;
}

.card-section {
  background-color: white;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
}

h3 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.table-wrapper {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e9f5;
}

table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  font-size: 14px;
}

th,
td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e8e9f5;
  font-weight: 500;
}

th {
  background-color: #e8f4f0;
  font-weight: 700;
  color: #323232;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

tr:last-child td {
  border-bottom: none;
}

tr:hover {
  background-color: #f9f9f9;
}

.login-history {
  max-height: 100px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #ccc transparent;
}

.login-history::-webkit-scrollbar {
  width: 6px;
}

.login-history::-webkit-scrollbar-track {
  background: transparent;
}

.login-history::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.login-timestamp {
  display: block;
  background-color: #f5f5f5;
  padding: 5px 8px;
  margin-bottom: 4px;
  border-radius: 4px;
  font-size: 13px;
  transition: background-color 0.2s;
  cursor: default;
}

.login-timestamp:hover {
  background-color: #e8f4f0;
  color: #42b983;
}

.login-timestamp:first-child {
  color: #505050;
  font-weight: 500;
}

.error {
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
}

.loading {
  text-align: center;
  padding: 24px;
  color: #505050;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #e8e9f5;
  margin-bottom: 1.5rem;
}

.no-data {
  text-align: center;
  padding: 24px;
  color: #505050;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding: 1rem;
  }

  .login-history {
    max-height: 80px;
  }
}
</style>